"use client";

import { useSearchPageBannersResponse } from "@faire/retailer-visitor-shared/components/Banners/DataFetching/useSearchPageBannersData";
import { useRawBannerData } from "@faire/retailer-visitor-shared/components/Banners/SearchBanners/RawBannerDataContextProvider";
import { SearchPageBannersDataContextProvider } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/ViewState/SearchPageBannersDataContext";
import { useSearchPageFilters } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageFilters";
import { useSearchPageProductSearchString } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";
import fetchSearchPageBanners from "@faire/web-api--source/endpoints/www/api/v2/products/search/banners/post";
import { ISearchCatNavBannersRequest } from "@faire/web-api--source/indigofair/data/ISearchCatNavBannersRequest";
import { PageType } from "@faire/web-api--source/indigofair/data/PageType";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import React, { ReactNode, useMemo } from "react";

export const DiscoverPageBannersUseQueryDataProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const query = useSearchPageProductSearchString();
  const filters = useSearchPageFilters();

  /**
   * In RSC, we fetch the banners data at the server and send it to the client
   * side via RawBannerDataContext. If the data is available, we use it directly
   * instead of fetching it again.
   */
  const serverData = useRawBannerData();

  const request = ISearchCatNavBannersRequest.build({
    query: query,
    filter_keys: filters,
    page_type: PageType.DISCOVER_PAGE,
  });

  // if the serverData is not available, we fetch it again
  const { data: clientData } = useQuery({
    queryKey: [request],
    queryFn: () => fetchSearchPageBanners(request),
    enabled: !!query && !serverData,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });

  const {
    bannersResponse,
    bannersDisplayData,
    replacedProductTilesCount,
    displayableBannersWithAdjustedRow,
  } = useSearchPageBannersResponse(serverData ?? clientData);

  const data = useMemo(
    () => ({
      bannersResponse,
      bannersDisplayData,
      replacedProductTilesCount,
      displayableBannersWithAdjustedRow,
    }),
    [
      bannersResponse,
      bannersDisplayData,
      replacedProductTilesCount,
      displayableBannersWithAdjustedRow,
    ]
  );

  return (
    <SearchPageBannersDataContextProvider searchBannersData={data}>
      {children}
    </SearchPageBannersDataContextProvider>
  );
};
